/**
 * userDashboardStore — learner dashboard slice (Zustand).
 *
 *   GET /api/learners/dashboard
 *   GET /api/learners/dashboard/continue-learning
 *   GET /api/learners/dashboard/news
 *   GET /api/learners/dashboard/recent-resources
 *   GET /api/learners/dashboard/recent-transactions
 */
import { apiRequest, normalizeList } from "./userUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const userDashboardInitialState = {
  userDashboard: null,
  userDashboardView: null,
  userDashboardLoading: false,
  userDashboardError: null,

  continueLearning: [],
  continueLearningLoading: false,
  continueLearningError: null,

  dashboardNews: [],
  dashboardNewsLoading: false,
  dashboardNewsError: null,

  recentResources: [],
  recentResourcesLoading: false,
  recentResourcesError: null,

  recentTransactions: [],
  recentTransactionsLoading: false,
  recentTransactionsError: null,
};

/** First key in `keys` that holds an array on `source` (else `[]`). */
function firstArray(source, keys) {
  for (const key of keys) {
    if (Array.isArray(source?.[key])) return source[key];
  }
  return [];
}

/**
 * Map the learner dashboard payload onto a stable view model so the UI does not
 * depend on the exact key names the API chooses. Accepts the flat legacy shape
 * (`allCourses`, `inCompletedCourses`, …) as well as nested/modern variants
 * (`data.courses`, `stats.completedCourses`, `continueLearning`, …).
 */
export function normalizeLearnerDashboard(payload) {
  const root = payload?.data && typeof payload.data === "object" ? payload.data : payload || {};
  const scope = { ...(payload || {}), ...root };
  const stats = scope.stats && typeof scope.stats === "object" ? scope.stats : scope;
  const pick = (keys) => {
    const direct = firstArray(scope, keys);
    return direct.length ? direct : firstArray(stats, keys);
  };

  const rawStats = scope.stats && typeof scope.stats === "object" ? scope.stats : null;
  /** First present numeric stat among `keys` (missing/blank → 0). */
  const stat = (keys) => {
    for (const key of keys) {
      const value = rawStats?.[key];
      if (value !== undefined && value !== null && value !== "") return Number(value) || 0;
    }
    return 0;
  };

  return {
    // The learner endpoint returns the profile under `summary`.
    user: scope.summary || scope.user || scope.learner || scope.profile || root.user || null,
    allCourses: pick(["allCourses", "all_courses", "courses", "enrolledCourses"]),
    inCompletedCourses: pick([
      "inCompletedCourses",
      "in_completed_courses",
      "inProgressCourses",
      "in_progress_courses",
      "continueLearning",
      "inProgress",
    ]),
    completedCourses: pick(["completedCourses", "completed_courses", "completed"]),
    pendingInvitaions: pick(["pendingInvitaions", "pendingInvitations", "pending_invitations"]),
    acceptedInvitations: pick([
      "acceptedInvitations",
      "accepted_invitations",
      "acceptedInvites",
    ]),
    wishlist: pick(["wishlist", "wishList"]),
    // `stats` is always an object so the dashboard cards can render without null checks.
    stats: {
      ...(rawStats || {}),
      totalCourses: stat(["totalCourses", "total_courses", "courses"]),
      completedCourses: stat(["completedCourses", "completed_courses", "completed"]),
      inProgressCourses: stat(["inProgressCourses", "in_progress_courses", "inProgress"]),
      notStartedCourses: stat(["notStartedCourses", "not_started_courses", "notStarted"]),
      totalSpent: stat(["totalSpent", "total_spent", "spent"]),
      totalDownloads: stat(["totalDownloads", "total_downloads", "downloads"]),
    },
    continueLearning: pick(["continueLearning", "continue_learning"]),
    recentEnrollments: pick(["recentEnrollments", "recent_enrollments", "enrollments"]),
    recentTransactions: pick(["recentTransactions", "recent_transactions", "transactions"]),
    recentResources: pick(["recentResources", "recent_resources", "resources"]),
    news: pick(["news", "latestNews", "articles"]),
  };
}

export function createUserDashboardActions(set, get) {
  async function fetchList(prefix, path, params = {}, keys = []) {
    set({ [`${prefix}Loading`]: true, [`${prefix}Error`]: null });
    try {
      const query = new URLSearchParams(params).toString();
      const data = await apiRequest(get, `${path}${query ? `?${query}` : ""}`);
      const { items, total, totalPages } = normalizeList(data, keys);
      set({
        [`${prefix}Loading`]: false,
        [prefix]: items,
        [`${prefix}Total`]: total,
        [`${prefix}Pages`]: totalPages,
      });
      return { success: true, items, total, totalPages };
    } catch (err) {
      const msg = toDisplayString(err.message, "Request failed");
      set({ [`${prefix}Loading`]: false, [`${prefix}Error`]: msg });
      return { success: false, error: msg };
    }
  }

  return {
    // GET /api/learners/dashboard
    fetchUserDashboard: async (params = {}) => {
      set({ userDashboardLoading: true, userDashboardError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const data = await apiRequest(
          get,
          `/api/learners/dashboard${query ? `?${query}` : ""}`
        );
        const dashboard = data?.data ?? data;
        const view = normalizeLearnerDashboard(dashboard);
        set({ userDashboardLoading: false, userDashboard: dashboard, userDashboardView: view });
        return { success: true, dashboard, view };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load dashboard");
        set({ userDashboardLoading: false, userDashboardError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/dashboard/continue-learning
    fetchContinueLearning: (params = {}) =>
      fetchList("continueLearning", "/api/learners/dashboard/continue-learning", params, [
        "courses",
      ]),

    // GET /api/learners/dashboard/news
    fetchDashboardNews: (params = {}) =>
      fetchList("dashboardNews", "/api/learners/dashboard/news", params, ["news", "articles"]),

    // GET /api/learners/dashboard/recent-resources
    fetchRecentResources: (params = {}) =>
      fetchList("recentResources", "/api/learners/dashboard/recent-resources", params, [
        "resources",
      ]),

    // GET /api/learners/dashboard/recent-transactions
    fetchRecentTransactions: (params = {}) =>
      fetchList("recentTransactions", "/api/learners/dashboard/recent-transactions", params, [
        "transactions",
      ]),
  };
}

const userDashboardActions = createUserDashboardActions;
export default userDashboardActions;
